<script setup lang="ts">
import { computed, ref } from 'vue'
import { useFocus } from '@vueuse/core'
import { vMaska } from 'maska/vue'

export type TaxIdFormat = 'ein' | 'ssn'

const TAX_ID_MASKS: Record<TaxIdFormat, string> = {
  ein: '##-#######', // XX-XXXXXXX (Employer Identification Number)
  ssn: '###-##-####', // XXX-XX-XXXX (Social Security Number)
}

const TAX_ID_PLACEHOLDERS: Record<TaxIdFormat, string> = {
  ein: '12-3456789',
  ssn: '123-45-6789',
}

const props = withDefaults(
  defineProps<{
    modelValue?: string | null
    description?: string
    disabled?: boolean
    error?: string
    id?: string
    label?: string
    name?: string
    placeholder?: string
    property?: string
    required?: boolean
    format?: TaxIdFormat
    v$?: any
    disabledVariant?: string
  }>(),
  {
    disabled: false,
    format: 'ein',
    disabledVariant: 'default',
    v$: {},
  },
)

const emit = defineEmits(['update:modelValue'])
const inputRef = ref<HTMLInputElement | null>(null)
const { focused: isFocused } = useFocus(inputRef)

const focusInput = () => {
  if (inputRef.value) inputRef.value.focus()
}

const mask = computed(() => TAX_ID_MASKS[props.format])
const placeholderText = computed(() => props.placeholder || TAX_ID_PLACEHOLDERS[props.format])

const value = computed({
  get: () => props.modelValue || '',
  set: (val: string) => {
    emit('update:modelValue', val || null)
  },
})

const error = computed(() => props.error || props.v$[props.property || props.name]?.$errors[0]?.$message)

const disabledVariants: Record<string, string> = {
  default: 'bg-gray-50 text-gray-500',
  light: 'bg-gray-50/50 text-gray-700',
  text: '!border-0 !rounded-none !shadow-none font-semibold !p-0',
}

const disabledClass = computed(() => {
  return props.disabled ? disabledVariants[props.disabledVariant] : ''
})
</script>

<template>
  <fieldset :id="id || property">
    <label :for="id" class="block cursor-pointer text-sm font-medium text-gray-700" @click.prevent="focusInput">
      <span>{{ label }}</span>
      <span v-if="required && disabledVariant !== 'text'">*</span>
    </label>
    <div class="mt-1">
      <input
        ref="inputRef"
        v-model="value"
        v-maska
        :data-maska="mask"
        class="block w-full placeholder:italic"
        :class="[
          'block w-full',
          error
            ? 'border-red-300 focus:border-red-300 focus:ring-red-200'
            : 'border-gray-300 shadow-sm focus:border-sky-300 focus:ring focus:ring-sky-200 focus:ring-opacity-50',
          'mt-1 rounded-md border',
          disabledClass,
        ]"
        :disabled="disabled"
        :id="id"
        :name="name || property"
        :placeholder="placeholderText"
        type="text"
        inputmode="numeric"
      />
    </div>
    <template v-if="error">
      <p class="mt-1 text-sm text-red-500">{{ error }}</p>
    </template>
    <template v-if="description">
      <p class="mt-1 text-sm text-gray-500" :id="`${id}-description`">{{ description }}</p>
    </template>
  </fieldset>
</template>
